import { useCallback, useRef } from 'react';
import { useLocation, useNavigate, useParams } from '@tanstack/react-router';
import { useBookmarkPageContent } from '../contexts/BookmarkPageContext';
import { getReadRoute } from './useReadUrl';
import { getListPathFromPathname } from '../utils/readerRouteUtils';

/**
 * Returns a function that navigates to the next bookmark in the list
 * after the given one is removed (archived/deleted).
 *
 * Strategy: try index+1 (next), fall back to index-1 (previous),
 * fall back to list view if no bookmarks remain.
 *
 * Uses a ref so the callback always reads fresh state from the
 * latest render without needing to be recreated.
 */
export function useNavigateToNextBookmark() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const params = useParams({ strict: false });
  const bookmarkPage = useBookmarkPageContent();

  const stateRef = useRef({ pathname, params, bookmarkPage });
  stateRef.current = { pathname, params, bookmarkPage };

  return useCallback(
    (removedBookmarkId: string) => {
      const { pathname, params, bookmarkPage } = stateRef.current;

      if (!bookmarkPage) {
        const listPath = getListPathFromPathname(pathname);
        navigate({ to: listPath as '/' });
        return;
      }

      const { bookmarks, setActiveBookmark } = bookmarkPage;
      const currentIndex = bookmarks.findIndex(
        (b) => String(b.id) === String(removedBookmarkId),
      );

      let nextBookmarkId: string | null = null;
      if (currentIndex !== -1) {
        if (currentIndex + 1 < bookmarks.length) {
          nextBookmarkId = bookmarks[currentIndex + 1].id;
        } else if (currentIndex - 1 >= 0) {
          nextBookmarkId = bookmarks[currentIndex - 1].id;
        }
      }

      if (!nextBookmarkId) {
        // No bookmarks left — go to list view
        const listPath = getListPathFromPathname(pathname);
        navigate({ to: listPath as '/' });
        return;
      }

      const isUrlBased = pathname.includes('/read/');

      if (isUrlBased) {
        const { toPattern, baseParams } = getReadRoute(pathname, params);
        navigate({
          to: toPattern,
          params: { ...baseParams, id: nextBookmarkId },
          search: (prev) => prev,
          replace: true,
        });
      } else {
        setActiveBookmark({ id: nextBookmarkId });
      }
    },
    [navigate],
  );
}
